import { Image as ExpoImage } from 'expo-image';
import { Heart, X } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  type GestureResponderEvent,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';

import { useToast } from '@/src/components/Toast';
import {
  useFollowState,
  useToggleFollow,
} from '@/src/features/profile/use-profile';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { openUserProfile } from '@/src/utils/profile-navigation';
import {
  subscribeToVehicleCommentReactionActors,
  subscribeToVehicleReactionActors,
} from '@/src/features/garage/garage-service';
import { recordSentryError } from '@/src/lib/sentry';

import {
  DEFAULT_REACTION,
  REACTIONS,
  subscribeToCommentReactionActors,
  subscribeToPostReactionActors,
  totalReactionCounts,
  type Reaction,
  type ReactionActor,
  type ReactionCounts,
} from './post-service';
import { getTopUniqueReactions } from './reaction-summary';

export {
  createReactionMutationId,
  reactionCountsEqual,
  shouldClearOptimisticReaction,
} from './reaction-optimistic';

export const reactionMeta: Record<Reaction, { emoji: string; label: string }> =
  {
    love: { emoji: '❤️', label: 'Love' },
    like: { emoji: '👍', label: 'Like' },
    dislike: { emoji: '👎', label: 'Dislike' },
    laugh: { emoji: '😂', label: 'Laugh' },
    wow: { emoji: '😮', label: 'Wow' },
    vomit: { emoji: '🤮', label: 'Vomit' },
    fire: { emoji: '🔥', label: 'Fire' },
    trash: { emoji: '🗑️', label: 'Trash' },
  };

export type ReactionTarget =
  | { kind: 'post'; postId: string }
  | { commentId: string; kind: 'comment'; postId: string }
  | { kind: 'vehicle'; vehicleId: string }
  | { commentId: string; kind: 'vehicle_comment'; vehicleId: string };
type PickerAnchor = {
  height: number;
  width: number;
  x: number;
  y: number;
};
type PickerLayout = {
  height: number;
  left: number;
  top: number;
  width: number;
};
const REACTION_PICKER_CLOSE_DELAY_MS = 80;
const TOUCH_PICKER_HEIGHT = 74;
const TOUCH_PICKER_WIDTH = 392;
const COMPACT_PICKER_WIDTH =
  REACTIONS.length * 44 + (REACTIONS.length - 1) * 2 + spacing.xs * 2;
const WEB_NO_SELECT_STYLE =
  Platform.OS === 'web'
    ? ({
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
        userSelect: 'none',
      } as unknown as ViewStyle)
    : null;

function preventNativeSelection(event: GestureResponderEvent) {
  event.stopPropagation();

  const nativeEvent = event.nativeEvent as {
    preventDefault?: () => void;
    stopPropagation?: () => void;
  };

  nativeEvent.preventDefault?.();
  nativeEvent.stopPropagation?.();
}

function stopNativePropagation(event: GestureResponderEvent) {
  event.stopPropagation();

  const nativeEvent = event.nativeEvent as {
    stopPropagation?: () => void;
  };

  nativeEvent.stopPropagation?.();
}

function reactionFromLocation(locationX: number, pickerWidth: number) {
  const clampedX = Math.max(0, Math.min(locationX, pickerWidth - 1));
  const index = Math.max(
    0,
    Math.min(
      REACTIONS.length - 1,
      Math.floor((clampedX / pickerWidth) * REACTIONS.length),
    ),
  );

  return REACTIONS[index];
}

function getTouchPickerLayout({
  anchor,
  windowSize,
}: {
  anchor: PickerAnchor | null;
  windowSize: { height: number; width: number };
}): PickerLayout {
  const width = Math.min(TOUCH_PICKER_WIDTH, windowSize.width - spacing.md * 2);
  const preferredTop = anchor
    ? anchor.y > TOUCH_PICKER_HEIGHT + spacing.sm
      ? anchor.y - TOUCH_PICKER_HEIGHT - spacing.xs
      : anchor.y + anchor.height + spacing.xs
    : windowSize.height - 160;
  const top = Math.max(
    spacing.md,
    Math.min(
      preferredTop,
      windowSize.height - TOUCH_PICKER_HEIGHT - spacing.lg,
    ),
  );
  const left = anchor
    ? Math.max(
        spacing.md,
        Math.min(anchor.x - spacing.xs, windowSize.width - width - spacing.md),
      )
    : Math.max(spacing.md, (windowSize.width - width) / 2);

  return { height: TOUCH_PICKER_HEIGHT, left, top, width };
}

function reactionFromPagePosition(
  event: GestureResponderEvent,
  pickerLayout: PickerLayout,
) {
  const nativeEvent = event.nativeEvent as {
    pageX?: number;
    pageY?: number;
  };
  const { pageX, pageY } = nativeEvent;

  if (typeof pageX !== 'number' || typeof pageY !== 'number') {
    return null;
  }

  return reactionFromPoint({ pickerLayout, x: pageX, y: pageY });
}

function reactionFromPoint({
  pickerLayout,
  x,
  y,
}: {
  pickerLayout: PickerLayout;
  x: number;
  y: number;
}) {
  const verticalSlop = spacing.md;
  const horizontalSlop = spacing.xs;

  if (
    y < pickerLayout.top - verticalSlop ||
    y > pickerLayout.top + pickerLayout.height + verticalSlop ||
    x < pickerLayout.left - horizontalSlop ||
    x > pickerLayout.left + pickerLayout.width + horizontalSlop
  ) {
    return null;
  }

  return reactionFromLocation(x - pickerLayout.left, pickerLayout.width);
}

export function applyReactionTransition({
  baseCounts,
  baseReaction,
  reaction,
}: {
  baseCounts: ReactionCounts;
  baseReaction: Reaction | null;
  reaction: Reaction | null;
}) {
  const counts = { ...baseCounts };

  if (baseReaction) {
    counts[baseReaction] = Math.max(0, counts[baseReaction] - 1);
  }

  if (reaction) {
    counts[reaction] += 1;
  }

  return {
    counts,
    total: totalReactionCounts(counts),
  };
}

export function CompactReactionButton({
  disabled,
  flushLeft = false,
  loading,
  onSelect,
  pickerOpensLeft = false,
  selectedReaction,
  testID,
}: {
  disabled?: boolean;
  flushLeft?: boolean;
  loading?: boolean;
  onSelect: (reaction: Reaction) => void;
  pickerOpensLeft?: boolean;
  selectedReaction: Reaction | null;
  testID?: string;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState<PickerAnchor | null>(null);
  const [useTouchPicker, setUseTouchPicker] = useState(Platform.OS !== 'web');
  const [dragReaction, setDragReaction] = useState<Reaction | null | undefined>(
    undefined,
  );
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerOverPickerRef = useRef(false);
  const pointerOverTriggerRef = useRef(false);
  const longPressOpenedRef = useRef(false);
  const skipNextPressRef = useRef(false);
  const shellRef = useRef<View>(null);
  const selectedMeta = selectedReaction ? reactionMeta[selectedReaction] : null;
  const active = Boolean(selectedMeta);
  const touchPickerLayout = useMemo(() => {
    if (!pickerAnchor) {
      return null;
    }

    return getTouchPickerLayout({
      anchor: pickerAnchor,
      windowSize: { height: windowHeight, width: windowWidth },
    });
  }, [pickerAnchor, windowHeight, windowWidth]);

  function clearCloseTimeout() {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }

  function openPicker() {
    if (disabled) {
      return;
    }

    clearCloseTimeout();

    if (useTouchPicker) {
      if (!shellRef.current) {
        setPickerAnchor(null);
        setPickerOpen(true);
        return;
      }

      shellRef.current.measureInWindow((x, y, width, height) => {
        setPickerAnchor({ height, width, x, y });
        setPickerOpen(true);
      });
      return;
    }

    setPickerOpen(true);
  }

  function closePickerSoon() {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      if (!pointerOverPickerRef.current && !pointerOverTriggerRef.current) {
        setPickerOpen(false);
      }

      closeTimeoutRef.current = null;
    }, REACTION_PICKER_CLOSE_DELAY_MS);
  }

  function handlePointerEnterTrigger() {
    if (Platform.OS !== 'web' || useTouchPicker) {
      return;
    }

    pointerOverTriggerRef.current = true;
    openPicker();
  }

  function handlePointerLeaveTrigger() {
    if (Platform.OS !== 'web' || useTouchPicker) {
      return;
    }

    pointerOverTriggerRef.current = false;
    closePickerSoon();
  }

  function handlePointerEnterPicker() {
    pointerOverPickerRef.current = true;
    clearCloseTimeout();
  }

  function handlePointerLeavePicker() {
    pointerOverPickerRef.current = false;
    closePickerSoon();
  }

  function selectReaction(reaction: Reaction) {
    onSelect(reaction);
    clearCloseTimeout();
    setDragReaction(undefined);
    setPickerOpen(false);
  }

  function handleTriggerPressOut(event: GestureResponderEvent) {
    if (!longPressOpenedRef.current) {
      return;
    }

    preventNativeSelection(event);

    const nextReaction =
      dragReaction !== undefined
        ? dragReaction
        : touchPickerLayout
          ? reactionFromPagePosition(event, touchPickerLayout)
          : null;
    longPressOpenedRef.current = false;
    setDragReaction(undefined);

    if (nextReaction) {
      selectReaction(nextReaction);
    }
  }

  function handleTriggerPressIn(event: GestureResponderEvent) {
    if (Platform.OS === 'web' && !useTouchPicker) {
      stopNativePropagation(event);
      return;
    }

    preventNativeSelection(event);
  }

  useEffect(() => {
    return clearCloseTimeout;
  }, []);

  useEffect(() => {
    if (disabled) {
      clearCloseTimeout();
      setDragReaction(undefined);
      setPickerOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(hover: none), (pointer: coarse)');
    const updateTouchPicker = () => setUseTouchPicker(mediaQuery.matches);

    updateTouchPicker();
    mediaQuery.addEventListener?.('change', updateTouchPicker);

    return () => {
      mediaQuery.removeEventListener?.('change', updateTouchPicker);
    };
  }, []);

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      typeof window === 'undefined' ||
      !pickerOpen ||
      !useTouchPicker ||
      !touchPickerLayout ||
      !longPressOpenedRef.current
    ) {
      return undefined;
    }

    const listenerOptions = { passive: false } as AddEventListenerOptions;
    const updateDragReaction = (x: number, y: number) => {
      setDragReaction(
        reactionFromPoint({ pickerLayout: touchPickerLayout, x, y }),
      );
    };
    const handleTouchMove = (event: TouchEvent) => {
      event.preventDefault();
      const touch = event.touches[0] ?? event.changedTouches[0];

      if (!touch) {
        setDragReaction(null);
        return;
      }

      updateDragReaction(touch.clientX, touch.clientY);
    };
    const handleTouchEnd = () => {
      setDragReaction(undefined);
    };

    window.addEventListener('touchmove', handleTouchMove, listenerOptions);
    window.addEventListener('touchcancel', handleTouchEnd, listenerOptions);

    return () => {
      window.removeEventListener('touchmove', handleTouchMove, listenerOptions);
      window.removeEventListener(
        'touchcancel',
        handleTouchEnd,
        listenerOptions,
      );
    };
  }, [pickerOpen, touchPickerLayout, useTouchPicker]);

  return (
    <View
      ref={shellRef}
      onPointerEnter={handlePointerEnterTrigger}
      onPointerLeave={handlePointerLeaveTrigger}
      style={[
        styles.compactShell,
        WEB_NO_SELECT_STYLE,
        flushLeft ? styles.compactShellFlushLeft : null,
      ]}
    >
      {pickerOpen && !useTouchPicker ? (
        <View
          onPointerEnter={handlePointerEnterPicker}
          onPointerLeave={handlePointerLeavePicker}
          style={[
            styles.pickerPopover,
            pickerOpensLeft || flushLeft ? styles.pickerPopoverOpensLeft : null,
          ]}
        >
          <ReactionPicker
            selectedReaction={selectedReaction}
            onSelect={selectReaction}
          />
        </View>
      ) : null}
      {pickerOpen && useTouchPicker ? (
        <ReactionPickerModal
          anchor={pickerAnchor}
          dragReaction={dragReaction}
          selectedReaction={selectedReaction}
          onClose={() => setPickerOpen(false)}
          onSelect={selectReaction}
        />
      ) : null}
      <Pressable
        accessibilityLabel={
          selectedMeta
            ? `Change or clear ${selectedMeta.label} reaction`
            : 'React'
        }
        accessibilityRole="button"
        delayLongPress={260}
        disabled={disabled}
        hitSlop={8}
        onPressIn={handleTriggerPressIn}
        onLongPress={(event) => {
          preventNativeSelection(event);
          longPressOpenedRef.current = true;
          skipNextPressRef.current = true;
          openPicker();
        }}
        onPressOut={handleTriggerPressOut}
        onPress={(event) => {
          event.stopPropagation();

          if (skipNextPressRef.current) {
            skipNextPressRef.current = false;
            return;
          }

          if (!disabled) {
            clearCloseTimeout();
            setPickerOpen(false);
            onSelect(DEFAULT_REACTION);
          }
        }}
        style={({ pressed }) => [
          styles.compactButton,
          WEB_NO_SELECT_STYLE,
          flushLeft ? styles.compactButtonFlushLeft : null,
          active ? styles.compactButtonActive : null,
          pressed && !disabled ? styles.compactButtonPressed : null,
          disabled ? styles.compactButtonDisabled : null,
        ]}
        testID={testID}
      >
        {loading ? (
          <ActivityIndicator color={colors.secondary} size="small" />
        ) : selectedMeta ? (
          <Text selectable={false} style={styles.compactEmoji}>
            {selectedMeta.emoji}
          </Text>
        ) : (
          <Heart
            color={colors.mutedText}
            fill="transparent"
            size={22}
            strokeWidth={2.4}
          />
        )}
      </Pressable>
    </View>
  );
}

function ReactionPickerModal({
  anchor,
  dragReaction,
  onClose,
  onSelect,
  selectedReaction,
}: {
  anchor: PickerAnchor | null;
  dragReaction?: Reaction | null;
  onClose: () => void;
  onSelect: (reaction: Reaction) => void;
  selectedReaction: Reaction | null;
}) {
  const windowSize = useWindowDimensions();
  const [touchReaction, setTouchReaction] = useState<Reaction | null>(null);
  const pickerLayout = getTouchPickerLayout({ anchor, windowSize });
  const pickerSelection =
    dragReaction !== undefined
      ? dragReaction
      : (touchReaction ?? selectedReaction);

  function updateTouchReaction(event: GestureResponderEvent) {
    preventNativeSelection(event);
    setTouchReaction(
      reactionFromLocation(event.nativeEvent.locationX, pickerLayout.width),
    );
  }

  function selectTouchReaction(event: GestureResponderEvent) {
    preventNativeSelection(event);

    const nextReaction =
      touchReaction ??
      reactionFromLocation(event.nativeEvent.locationX, pickerLayout.width);

    onSelect(nextReaction);
  }

  return (
    <Modal animationType="fade" transparent visible onRequestClose={onClose}>
      <Pressable
        accessibilityLabel="Close reaction picker"
        accessibilityRole="button"
        onLongPress={preventNativeSelection}
        onPress={onClose}
        onPressIn={preventNativeSelection}
        style={[styles.touchPickerBackdrop, WEB_NO_SELECT_STYLE]}
      >
        <Pressable
          onLongPress={preventNativeSelection}
          onMoveShouldSetResponder={() => true}
          onMoveShouldSetResponderCapture={() => true}
          onResponderGrant={updateTouchReaction}
          onResponderMove={updateTouchReaction}
          onResponderRelease={selectTouchReaction}
          onResponderTerminate={() => setTouchReaction(null)}
          onStartShouldSetResponder={() => true}
          onStartShouldSetResponderCapture={() => true}
          onPressIn={preventNativeSelection}
          style={[
            styles.touchPickerSheet,
            WEB_NO_SELECT_STYLE,
            {
              left: pickerLayout.left,
              top: pickerLayout.top,
              width: pickerLayout.width,
            },
          ]}
        >
          <ReactionPicker
            fill
            selectedReaction={pickerSelection}
            onSelect={onSelect}
          />
        </Pressable>
      </Pressable>
    </Modal>
  );
}

export function ReactionPicker({
  fill = false,
  onSelect,
  selectedReaction,
}: {
  fill?: boolean;
  onSelect: (reaction: Reaction) => void;
  selectedReaction: Reaction | null;
}) {
  const [hoveredReaction, setHoveredReaction] = useState<Reaction | null>(null);

  return (
    <View
      style={[
        styles.picker,
        WEB_NO_SELECT_STYLE,
        fill ? styles.pickerFill : null,
      ]}
    >
      {REACTIONS.map((reaction) => {
        const selected = selectedReaction === reaction;
        const active = selected || hoveredReaction === reaction;
        const meta = reactionMeta[reaction];

        return (
          <Pressable
            key={reaction}
            accessibilityLabel={`${selected ? 'Clear' : 'Set'} ${meta.label} reaction`}
            accessibilityRole="button"
            delayLongPress={260}
            onLongPress={(event) => {
              preventNativeSelection(event);
              onSelect(reaction);
            }}
            onPress={(event) => {
              preventNativeSelection(event);
              onSelect(reaction);
            }}
            onHoverIn={
              Platform.OS === 'web'
                ? () => setHoveredReaction(reaction)
                : undefined
            }
            onHoverOut={
              Platform.OS === 'web'
                ? () =>
                    setHoveredReaction((current) =>
                      current === reaction ? null : current,
                    )
                : undefined
            }
            style={({ pressed }) => [
              styles.pickerOption,
              WEB_NO_SELECT_STYLE,
              active ? styles.pickerOptionActive : null,
              selected ? styles.pickerOptionSelected : null,
              pressed ? styles.pickerOptionPressed : null,
            ]}
          >
            <Text
              selectable={false}
              style={[
                styles.pickerEmoji,
                active ? styles.pickerEmojiActive : null,
              ]}
            >
              {meta.emoji}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

export function ReactionSummaryButton({
  counts,
  currentUid,
  selectedReaction,
  target,
}: {
  counts: ReactionCounts;
  currentUid: string | undefined;
  selectedReaction: Reaction | null;
  target: ReactionTarget;
}) {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const topReactions = getTopUniqueReactions(counts);
  const total = totalReactionCounts(counts);
  const canOpenDetails = Boolean(currentUid);

  if (total === 0) {
    return null;
  }

  return (
    <>
      <Pressable
        accessibilityLabel="View reactions"
        accessibilityRole="button"
        onPress={(event) => {
          event.stopPropagation();

          if (canOpenDetails) {
            setDetailsVisible(true);
          }
        }}
        style={({ pressed }) => [
          styles.summaryButton,
          pressed && canOpenDetails ? styles.summaryButtonPressed : null,
        ]}
      >
        <View style={styles.stackIcons}>
          {topReactions.map((reaction, index) => {
            const meta = reactionMeta[reaction];

            return (
              <View
                key={reaction}
                style={[
                  styles.stackIcon,
                  index > 0 ? styles.stackIconOverlap : null,
                ]}
              >
                <Text selectable={false} style={styles.stackEmoji}>
                  {meta.emoji}
                </Text>
              </View>
            );
          })}
        </View>
      </Pressable>
      {canOpenDetails ? (
        <ReactionDetailsModal
          counts={counts}
          currentUid={currentUid}
          target={target}
          visible={detailsVisible}
          onClose={() => setDetailsVisible(false)}
        />
      ) : null}
    </>
  );
}

function useReactionActors({
  target,
  visible,
}: {
  target: ReactionTarget;
  visible: boolean;
}) {
  const [actors, setActors] = useState<ReactionActor[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const commentId =
    target.kind === 'comment' || target.kind === 'vehicle_comment'
      ? target.commentId
      : undefined;
  const kind = target.kind;
  const postId =
    target.kind === 'post' || target.kind === 'comment'
      ? target.postId
      : undefined;
  const vehicleId =
    target.kind === 'vehicle' || target.kind === 'vehicle_comment'
      ? target.vehicleId
      : undefined;

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    setActors([]);
    setError(null);
    setLoading(true);

    const handleError = (nextError: Error) => {
      recordSentryError(nextError, `reaction_details_load_failed:${kind}`);
      setError(nextError);
      setLoading(false);
    };
    const handleNext = (nextActors: ReactionActor[]) => {
      setActors(nextActors);
      setLoading(false);
    };

    if (kind === 'post') {
      if (!postId) {
        handleError(new Error('Post ID is required to load reactions.'));
        return undefined;
      }

      return subscribeToPostReactionActors({
        postId,
        onError: handleError,
        onNext: handleNext,
      });
    }

    if (kind === 'vehicle') {
      if (!vehicleId) {
        handleError(new Error('Vehicle ID is required to load reactions.'));
        return undefined;
      }

      return subscribeToVehicleReactionActors({
        vehicleId,
        onError: handleError,
        onNext: handleNext,
      });
    }

    if (!commentId) {
      handleError(new Error('Comment ID is required to load reactions.'));
      return undefined;
    }

    if (kind === 'comment') {
      if (!postId) {
        handleError(new Error('Post ID is required to load reactions.'));
        return undefined;
      }

      return subscribeToCommentReactionActors({
        commentId,
        postId,
        onError: handleError,
        onNext: handleNext,
      });
    }

    if (!commentId || !vehicleId) {
      handleError(new Error('Comment ID is required to load reactions.'));
      return undefined;
    }

    return subscribeToVehicleCommentReactionActors({
      commentId,
      vehicleId,
      onError: handleError,
      onNext: handleNext,
    });
  }, [commentId, kind, postId, vehicleId, visible]);

  return { actors, error, loading };
}

function ReactionActorRow({
  actor,
  currentUid,
  onOpenProfile,
}: {
  actor: ReactionActor;
  currentUid: string | undefined;
  onOpenProfile: (uid: string) => void;
}) {
  const { showError, showSuccess } = useToast();
  const followState = useFollowState(currentUid, actor.uid);
  const toggleFollow = useToggleFollow(currentUid, actor.uid);
  const initial = actor.fullName.slice(0, 1).toUpperCase();
  const canFollow = Boolean(currentUid && currentUid !== actor.uid);
  const followActionPending = followState.loading || toggleFollow.isPending;

  function updateFollow(nextFollowing: boolean) {
    if (!canFollow || followActionPending) {
      return;
    }

    toggleFollow.mutate(
      { following: nextFollowing },
      {
        onError: (error) => {
          showError(
            error,
            nextFollowing
              ? 'Unable to follow this profile.'
              : 'Unable to unfollow this profile.',
          );
        },
        onSuccess: (result) => {
          if (result === 'queued') {
            showSuccess(`${nextFollowing ? 'Follow' : 'Unfollow'} queued.`);
            return;
          }

          showSuccess(
            nextFollowing
              ? `You are now following ${actor.fullName}.`
              : `You unfollowed ${actor.fullName}.`,
            nextFollowing ? 'Following' : 'Unfollowed',
          );
        },
      },
    );
  }

  return (
    <Pressable
      accessibilityLabel={`View ${actor.fullName}'s profile`}
      accessibilityRole="button"
      onPress={() => onOpenProfile(actor.uid)}
      style={({ pressed }) => [
        styles.actorRow,
        pressed ? styles.actorRowPressed : null,
      ]}
    >
      <View style={styles.actorAvatarWrap}>
        <View style={styles.actorAvatar}>
          {actor.photoURL ? (
            <ExpoImage
              accessibilityLabel=""
              cachePolicy="memory-disk"
              contentFit="cover"
              source={{ uri: actor.photoURL }}
              style={styles.actorAvatarImage}
            />
          ) : (
            <Text style={styles.actorAvatarText}>{initial}</Text>
          )}
        </View>
        <View style={styles.actorReactionBadge}>
          <Text style={styles.actorReactionEmoji}>
            {reactionMeta[actor.reaction].emoji}
          </Text>
        </View>
      </View>
      <View style={styles.actorTextWrap}>
        <Text numberOfLines={1} style={styles.actorName}>
          {actor.fullName}
        </Text>
      </View>
      {canFollow ? (
        <Pressable
          accessibilityLabel={
            followState.following
              ? `Unfollow ${actor.fullName}`
              : `Follow ${actor.fullName}`
          }
          accessibilityRole="button"
          disabled={followActionPending}
          onPress={(event) => {
            event.stopPropagation();
            updateFollow(!followState.following);
          }}
          style={({ pressed }) => [
            styles.actorFollowButton,
            followState.following ? styles.actorFollowButtonSecondary : null,
            pressed && !followActionPending
              ? styles.actorFollowButtonPressed
              : null,
            followActionPending ? styles.actorFollowButtonDisabled : null,
          ]}
        >
          {followActionPending ? (
            <ActivityIndicator
              color={followState.following ? colors.secondary : colors.surface}
              size="small"
            />
          ) : (
            <Text
              style={[
                styles.actorFollowButtonText,
                followState.following
                  ? styles.actorFollowButtonTextSecondary
                  : null,
              ]}
            >
              {followState.following ? 'Following' : 'Follow'}
            </Text>
          )}
        </Pressable>
      ) : null}
    </Pressable>
  );
}

export function ReactionDetailsModal({
  counts,
  currentUid,
  onActorProfilePress,
  onClose,
  target,
  visible,
}: {
  counts: ReactionCounts;
  currentUid: string | undefined;
  onActorProfilePress?: (targetUid: string) => void;
  onClose: () => void;
  target: ReactionTarget;
  visible: boolean;
}) {
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && width >= 900;
  const tabs = useMemo(() => {
    return [
      {
        count: totalReactionCounts(counts),
        label: 'All',
        value: 'all' as const,
      },
      ...REACTIONS.filter((reaction) => counts[reaction] > 0).map(
        (reaction) => ({
          count: counts[reaction],
          label: reactionMeta[reaction].emoji,
          reaction,
          value: reaction,
        }),
      ),
    ];
  }, [counts]);
  const [selectedTab, setSelectedTab] =
    useState<(typeof tabs)[number]['value']>('all');
  const { actors, error, loading } = useReactionActors({ target, visible });
  const filteredActors = actors
    .filter((actor) => selectedTab === 'all' || actor.reaction === selectedTab)
    .slice(0, 50);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setSelectedTab('all');
  }, [visible]);

  useEffect(() => {
    if (
      selectedTab !== 'all' &&
      !tabs.some((tab) => tab.value === selectedTab)
    ) {
      setSelectedTab('all');
    }
  }, [selectedTab, tabs]);

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable
        accessibilityLabel="Close reactions"
        accessibilityRole="button"
        onPress={onClose}
        style={styles.modalBackdrop}
      >
        <Pressable
          style={[
            styles.detailsSheet,
            isDesktopWeb
              ? styles.detailsDesktopSheet
              : styles.detailsMobileSheet,
          ]}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.sheetHandle} />
          <View style={styles.detailsHeader}>
            <Text style={styles.detailsTitle}>Reactions</Text>
            <Pressable
              accessibilityLabel="Close reactions"
              accessibilityRole="button"
              onPress={onClose}
              style={styles.closeButton}
            >
              <X color={colors.text} size={20} strokeWidth={2.4} />
            </Pressable>
          </View>
          <View style={styles.tabs}>
            {tabs.map((tab) => {
              const active = selectedTab === tab.value;
              const reaction =
                tab.value === 'all' ? null : (tab.value as Reaction);

              return (
                <Pressable
                  key={tab.value}
                  accessibilityRole="button"
                  onPress={() => setSelectedTab(tab.value)}
                  style={[styles.tab, active ? styles.tabActive : null]}
                >
                  <Text
                    style={[
                      styles.tabText,
                      active ? styles.tabTextActive : null,
                    ]}
                  >
                    {reaction ? reactionMeta[reaction].emoji : tab.label}{' '}
                    {tab.count}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          {loading ? (
            <View style={styles.detailsStatus}>
              <ActivityIndicator color={colors.primary} />
              <Text style={styles.detailsStatusText}>Loading reactions...</Text>
            </View>
          ) : null}
          {error ? (
            <Text style={styles.detailsError}>
              Unable to load reaction details.
            </Text>
          ) : null}
          {!loading && !error ? (
            <FlatList
              contentContainerStyle={styles.actorList}
              data={filteredActors}
              keyExtractor={(actor) => `${actor.uid}:${actor.reaction}`}
              ListEmptyComponent={
                <Text style={styles.detailsStatusText}>No reactions yet.</Text>
              }
              renderItem={({ item }) => {
                return (
                  <ReactionActorRow
                    actor={item}
                    currentUid={currentUid}
                    onOpenProfile={(targetUid) => {
                      onClose();
                      if (onActorProfilePress) {
                        onActorProfilePress(targetUid);
                        return;
                      }

                      openUserProfile({ currentUid, targetUid });
                    }}
                  />
                );
              }}
            />
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  actorAvatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: 24,
    height: 48,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 48,
  },
  actorAvatarImage: {
    height: '100%',
    width: '100%',
  },
  actorAvatarText: {
    color: colors.secondary,
    fontSize: 17,
    fontWeight: '900',
  },
  actorAvatarWrap: {
    position: 'relative',
  },
  actorFollowButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    borderRadius: 999,
    borderWidth: 1,
    minHeight: 34,
    justifyContent: 'center',
    minWidth: 92,
    paddingHorizontal: spacing.md,
  },
  actorFollowButtonDisabled: {
    opacity: 0.7,
  },
  actorFollowButtonPressed: {
    transform: [{ scale: 0.97 }],
  },
  actorFollowButtonSecondary: {
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
  },
  actorFollowButtonText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
  },
  actorFollowButtonTextSecondary: {
    color: colors.secondary,
  },
  actorList: {
    gap: spacing.sm,
    paddingBottom: spacing.xl,
  },
  actorName: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '900',
  },
  actorReactionBadge: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.surface,
    borderRadius: 999,
    borderWidth: 2,
    bottom: -2,
    height: 22,
    justifyContent: 'center',
    position: 'absolute',
    right: -3,
    width: 22,
  },
  actorReactionEmoji: {
    fontSize: 12,
    lineHeight: 16,
  },
  actorRow: {
    alignItems: 'center',
    borderRadius: 8,
    flexDirection: 'row',
    gap: spacing.md,
    paddingVertical: spacing.sm,
  },
  actorRowPressed: {
    backgroundColor: colors.surfaceTint,
  },
  actorTextWrap: {
    flex: 1,
    gap: 2,
  },
  closeButton: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: 38,
    justifyContent: 'center',
    width: 38,
  },
  compactButton: {
    alignItems: 'center',
    borderColor: 'transparent',
    borderRadius: 999,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 32,
  },
  compactButtonFlushLeft: {
    alignItems: 'flex-start',
    width: 24,
  },
  compactButtonActive: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  compactButtonDisabled: {
    opacity: 0.6,
  },
  compactButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  compactEmoji: {
    fontSize: 22,
    lineHeight: 28,
  },
  compactShell: {
    position: 'relative',
    zIndex: 3,
  },
  compactShellFlushLeft: {
    marginLeft: -12,
  },
  detailsError: {
    color: colors.danger,
    fontSize: 14,
    fontWeight: '800',
    paddingVertical: spacing.md,
  },
  detailsHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  detailsSheet: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    gap: spacing.md,
    minHeight: 320,
    padding: spacing.md,
  },
  detailsDesktopSheet: {
    borderRadius: 18,
    maxHeight: 640,
    maxWidth: 680,
    width: '92%',
  },
  detailsMobileSheet: {
    borderRadius: 18,
    maxHeight: '72%',
    maxWidth: 680,
    width: '98%',
  },
  detailsStatus: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xl,
  },
  detailsStatusText: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '700',
  },
  detailsTitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '900',
  },
  modalBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.36)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.sm,
  },
  picker: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: 'row',
    gap: 2,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
  },
  pickerFill: {
    alignSelf: 'stretch',
    justifyContent: 'space-between',
    width: '100%',
  },
  pickerEmoji: {
    fontSize: 22,
    lineHeight: 28,
  },
  pickerEmojiActive: {
    fontSize: 30,
    lineHeight: 34,
  },
  pickerOption: {
    alignItems: 'center',
    borderColor: 'transparent',
    borderRadius: 999,
    borderWidth: 1,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  pickerOptionActive: {
    backgroundColor: colors.surfaceTint,
    transform: [{ scale: 1.28 }, { translateY: -4 }],
    zIndex: 2,
  },
  pickerOptionPressed: {
    backgroundColor: colors.surfaceTint,
    transform: [{ scale: 1.18 }, { translateY: -2 }],
  },
  pickerOptionSelected: {
    borderColor: colors.primary,
  },
  pickerPopover: {
    backgroundColor: colors.surface,
    borderRadius: 999,
    bottom: 42,
    elevation: 6,
    left: 0,
    position: 'absolute',
    shadowColor: '#000',
    shadowOffset: { height: 8, width: 0 },
    shadowOpacity: 0.14,
    shadowRadius: 18,
    width: COMPACT_PICKER_WIDTH,
    zIndex: 20,
  },
  pickerPopoverOpensLeft: {
    left: -COMPACT_PICKER_WIDTH + 32,
  },
  sheetHandle: {
    alignSelf: 'center',
    backgroundColor: colors.border,
    borderRadius: 999,
    height: 5,
    width: 52,
  },
  stackCount: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '900',
  },
  stackCountSelected: {
    color: colors.mutedText,
  },
  stackEmoji: {
    fontSize: 14,
    lineHeight: 18,
  },
  stackIcon: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.surface,
    borderRadius: 999,
    borderWidth: 2,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  stackIconOverlap: {
    marginLeft: -7,
  },
  stackIcons: {
    flexDirection: 'row',
  },
  summaryButton: {
    alignItems: 'center',
    borderRadius: 999,
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 32,
    paddingHorizontal: spacing.xs,
  },
  summaryButtonPressed: {
    backgroundColor: colors.surfaceTint,
  },
  touchPickerBackdrop: {
    backgroundColor: 'transparent',
    flex: 1,
  },
  touchPickerSheet: {
    alignItems: 'center',
    backgroundColor: colors.surfacePressed,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: TOUCH_PICKER_HEIGHT,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    position: 'absolute',
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { height: 8, width: 0 },
    shadowOpacity: 0.14,
    shadowRadius: 18,
  },
  tab: {
    alignItems: 'center',
    borderBottomColor: 'transparent',
    borderBottomWidth: 3,
    flexDirection: 'row',
    gap: spacing.xs,
    paddingBottom: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  tabActive: {
    borderBottomColor: colors.secondary,
  },
  tabText: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '900',
  },
  tabTextActive: {
    color: colors.secondary,
  },
  tabs: {
    borderBottomColor: 'rgba(108, 91, 167, 0.22)',
    borderBottomWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
});
